import { Input as InputPrimitive } from '@base-ui/react/input'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/customer/lib/utils'

/**
 * Portal text input.
 *
 * Two visual variants:
 *
 * - `box` (default): the Snoots poster-outline box - `border-2
 *   border-foreground` on a white fill, rounded corners, focus ring.
 *   Used across signed-out forms (sign-in, registration, checkout)
 *   where the input is a page-level primary control.
 * - `underline`: bare bottom-rule input, no box, no fill. Used inside
 *   secondary contexts like `EditableField` where the input sits
 *   inside another bordered surface and a nested box would compound
 *   visual weight.
 *
 * Focus state changes colour, not thickness, to avoid layout shift
 * when tabbing between inputs.
 */
const inputVariants = cva(
  'w-full min-w-0 bg-transparent text-body-md text-[15px] transition-colors outline-none placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-semibold file:text-foreground [&[type=date]]:text-foreground [&[type=date][value=""]]:text-muted-foreground',
  {
    variants: {
      variant: {
        box: 'h-12 rounded-lg border-2 border-input bg-background px-4 focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/30',
        underline:
          'h-8 border-b-[1.75px] border-neutral-300 px-0.5 mx-1.5 focus-visible:border-ring aria-invalid:border-destructive',
      },
    },
    defaultVariants: { variant: 'box' },
  }
)

function Input({
  className,
  type,
  variant,
  ...props
}: React.ComponentProps<'input'> & VariantProps<typeof inputVariants>) {
  return (
    <InputPrimitive
      type={type}
      data-slot="input"
      className={cn(inputVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Input, inputVariants }
